// src/components/settings/SettingsModules.jsx
import React, { useEffect, useMemo, useState } from 'react'
import { apiFetch } from '../../api.js'
import { CATEGORIES } from '../../modules/registry.js'

export default function SettingsModules({ onFlash }) {
  const [loading, setLoading] = useState(true)
  const [modules, setModules] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    apiFetch('/api/settings/modules')
      .then((res) => {
        if (!alive) return
        setModules(res?.modules || [])
      })
      .catch(() => {
        if (!alive) return
        onFlash?.('❌ Erreur chargement modules')
      })
      .finally(() => {
        if (!alive) return
        setLoading(false)
      })
    return () => { alive = false }
  }, [onFlash])

  const modulesByCategory = useMemo(() => {
    const map = {}
    for (const c of CATEGORIES) map[c.key] = []
    for (const m of modules) {
      const cat = m.category || 'other'
      if (!map[cat]) map[cat] = []
      map[cat].push(m)
    }
    return map
  }, [modules])

  const toggleModule = (key, enabled) => {
    setModules((prev) => prev.map((m) => (m.key === key ? { ...m, enabled } : m)))
  }

  const applyPreset = (presetKey) => {
    const preset = {
      commerce: new Set(['sales', 'orders', 'customers', 'products', 'purchases', 'expenses', 'relances', 'analytics']),
      services: new Set(['projects', 'prestations', 'agenda', 'customers', 'expenses', 'analytics']),
      stock: new Set(['products', 'purchases', 'expenses', 'analytics']),
      full: new Set(modules.map((m) => m.key)),
    }[presetKey]

    if (!preset) return
    setModules((prev) => prev.map((m) => ({ ...m, enabled: preset.has(m.key) })))
  }

  const save = async () => {
    setSaving(true)
    try {
      const payload = { modules: {} }
      for (const m of modules) {
        payload.modules[m.key] = { enabled: m.enabled !== false, config: m.config || {} }
      }
      const res = await apiFetch('/api/settings/modules', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      setModules(res?.modules || [])
      window.dispatchEvent(new Event('tenant-modules-updated'))
      onFlash?.('✅ Modules mis à jour')
    } catch (err) {
      onFlash?.('❌ Erreur sauvegarde modules')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-500">Chargement des modules…</div>
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-xs text-slate-500 mr-2">Presets:</div>
        <button
          type="button"
          onClick={() => applyPreset('commerce')}
          className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50"
        >
          Commerce
        </button>
        <button
          type="button"
          onClick={() => applyPreset('services')}
          className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50"
        >
          Prestations
        </button>
        <button
          type="button"
          onClick={() => applyPreset('stock')}
          className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50"
        >
          Stock & Achats
        </button>
        <button
          type="button"
          onClick={() => applyPreset('full')}
          className="rounded-lg border px-3 py-1 text-xs hover:bg-slate-50"
        >
          Tout activer
        </button>
      </div>

      {CATEGORIES.map((cat) => (
        <section key={cat.key} className="rounded-xl border bg-white p-4 space-y-3">
          <div className="text-sm font-semibold">{cat.label}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(modulesByCategory[cat.key] || []).map((m) => (
              <label key={m.key} className="flex items-center justify-between rounded-lg border px-3 py-2">
                <div>
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-xs text-slate-500">{m.key}</div>
                </div>
                <input
                  type="checkbox"
                  checked={m.enabled !== false}
                  onChange={(e) => toggleModule(m.key, e.target.checked)}
                  className="h-4 w-4"
                />
              </label>
            ))}
          </div>
        </section>
      ))}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-slate-900 text-white px-4 py-2 text-sm disabled:opacity-50"
        >
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>
    </div>
  )
}
